import { useEffect, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./icon-button";
import { classNames } from "./internal/class-names";
import { tabbableElements } from "./internal/focus";
import { OperationalIcon } from "./operational-icon";

export interface DialogProps {
  readonly open: boolean;
  /**
   * Requested close. The dialog itself never decides to close: Escape and the
   * dismiss control both route through here so the owner stays in control.
   */
  readonly onClose: () => void;
  readonly title: ReactNode;
  /** Footer controls, typically Buttons. */
  readonly footer?: ReactNode | undefined;
  /** Set false to keep the dialog open on Escape (e.g. a blocking confirm). */
  readonly closeOnEscape?: boolean | undefined;
  /** Accessible name for the dismiss control. */
  readonly dismissLabel?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "small" | "medium" | "large" | "xlarge" | undefined;
  readonly mobilePresentation?: "dialog" | "fullscreen" | undefined;
  readonly children: ReactNode;
}

/**
 * Modal dialog with a hand-rolled focus trap.
 *
 * On open it captures the previously focused element and moves focus inside; Tab
 * and Shift+Tab wrap within the dialog; Escape requests close; on close focus is
 * restored to the opener. This is one of the two components (with `Tabs`) a
 * headless primitive library would replace — the props here are the contract to
 * preserve across that swap.
 */
export function Dialog({
  children,
  className,
  closeOnEscape = true,
  dismissLabel = "Close dialog",
  footer,
  onClose,
  open,
  size = "medium",
  title,
  mobilePresentation = "dialog",
}: DialogProps): ReactNode {
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const activeElement = document.activeElement;
    openerRef.current = activeElement instanceof HTMLElement ? activeElement : null;

    const dialog = dialogRef.current;
    // Focus the modal surface itself first. Focusing the first control used to
    // open its tooltip during mount (usually the dismiss action), obscuring the
    // title before the operator had interacted with the dialog.
    dialog?.focus();

    return () => {
      openerRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Escape") {
      if (closeOnEscape) {
        event.stopPropagation();
        onClose();
      }
      return;
    }

    if (event.key !== "Tab") return;

    const dialog = dialogRef.current;
    if (dialog === null) return;
    const focusables = tabbableElements(dialog);
    if (focusables.length === 0) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (
      event.shiftKey &&
      (active === dialog || active === first || !dialog.contains(active))
    ) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  const content = (
    <div
      className="thw-dialog-overlay"
      data-thw-portal="dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        aria-labelledby={titleId}
        aria-modal="true"
        className={classNames(
          "thw-dialog",
          `thw-dialog--${size}`,
          mobilePresentation === "fullscreen" && "thw-dialog--mobile-fullscreen",
          className,
        )}
        onKeyDown={handleKeyDown}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="thw-dialog__header">
          <h2 className="thw-dialog__title" id={titleId}>
            {title}
          </h2>
          <IconButton
            className="thw-icon-button--dismiss"
            icon={<OperationalIcon name="close" />}
            label={dismissLabel}
            onClick={(event) => {
              // Some dialog bodies host maps and drag-enabled controls. Keep
              // their pointer/click handlers from swallowing or replaying the
              // dismiss interaction into the content below the header.
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
            onPointerDown={(event) => { event.stopPropagation(); }}
            title={dismissLabel}
            type="button"
            variant="ghost"
          />
        </div>
        <div className="thw-dialog__body">{children}</div>
        {footer === undefined ? null : <div className="thw-dialog__footer">{footer}</div>}
      </div>
    </div>
  );
  return typeof document === "undefined"
    ? content
    : createPortal(content, document.body);
}
